import { useState } from "react";

const formatCoordinate = (value) => {
	const coordinate = Number(value);
	if (!Number.isFinite(coordinate)) {
		return "—";
	}
	return coordinate.toFixed(7);
};

const formatScale = (value) => {
	const scale = Number(value);
	if (!Number.isFinite(scale)) {
		return "—";
	}
	return `${scale} ft (${scale * 2} ft × ${scale * 2} ft)`;
};

const AerialImageOption = ({ image, isSelected, onSelect }) => {
	const [showDetails, setShowDetails] = useState(false);
	const isPending = Boolean(image.pending);
	const metadata = image.metadata;
	const originLat = isPending ? image.originLat : metadata?.originLat;
	const originLon = isPending ? image.originLon : metadata?.originLon;
	const scale = isPending ? image.scale : metadata?.scale;
	const hasCoordinates = Number.isFinite(Number(originLat)) && Number.isFinite(Number(originLon));

	return (
		<div
			className={`rounded-lg border p-3 ${
				isPending
					? "border-gray-300 bg-gray-100"
					: isSelected
						? "border-blue-600 bg-blue-50"
						: "border-gray-200 hover:border-blue-400"
			}`}>
			<div className="flex items-start justify-between gap-2">
				{isPending ? (
					<div className="min-w-0 flex-1 text-left text-gray-500">
						<div className="space-y-1">
							<p className="font-semibold">
								Coordinates: {formatCoordinate(originLat)}, {formatCoordinate(originLon)}
							</p>
							<p className="text-sm">Scale: {formatScale(scale)}</p>
						</div>
					</div>
				) : (
					<button type="button" className="min-w-0 flex-1 text-left" onClick={() => onSelect(image.dropboxId)}>
						{metadata || hasCoordinates ? (
							<div className="space-y-1">
								<p className="font-semibold">
									Coordinates: {formatCoordinate(originLat)}, {formatCoordinate(originLon)}
								</p>
								<p className="text-sm text-gray-700">Scale: {formatScale(scale)}</p>
							</div>
						) : (
							<p className="font-semibold break-all">{image.name}</p>
						)}
					</button>
				)}
				<div className="flex shrink-0 flex-col items-end gap-1">
					{isPending ? (
						<span className="rounded bg-amber-200 px-2 py-1 text-xs font-bold uppercase tracking-wide text-amber-900">Requested</span>
					) : isSelected ? (
						<span className="text-xs font-semibold text-blue-700">Selected</span>
					) : null}
					<button
						type="button"
						className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-blue-100"
						aria-expanded={showDetails}
						onClick={() => setShowDetails((currentValue) => !currentValue)}>
						{showDetails ? "Hide details" : "Details"}
					</button>
				</div>
			</div>
			{showDetails ? (
				isPending ? (
					<p className="mt-2 text-xs text-gray-700">Status: image request is processing</p>
				) : metadata ? (
					<div className="mt-2 space-y-1 text-xs text-gray-700">
						<p className="break-all">File: {image.name || "—"}</p>
						<p>Source: {metadata.source || "—"}</p>
						<p>Scale: {formatScale(metadata.scale)}</p>
						<p>Taken: {metadata.imageTakenTime || "—"}</p>
						<p>
							Coordinates: {formatCoordinate(metadata.originLat)}, {formatCoordinate(metadata.originLon)}
						</p>
						<p>Requested by: {metadata.requestBy || "—"}</p>
					</div>
				) : (
					<p className="mt-2 text-xs text-gray-500">No georeference metadata found for this image.</p>
				)
			) : null}
		</div>
	);
};

const AerialImagePickerModal = ({ images, imageRequests, selectedDropboxId, onSelectImage, onClose }) => {
	const pendingImages = (imageRequests || []).map((imageRequest) => ({
		pending: true,
		id: imageRequest.id,
		originLat: imageRequest.originLat,
		originLon: imageRequest.originLon,
		scale: imageRequest.scale,
	}));
	const pickerItems = [...(images || []), ...pendingImages];

	return (
		<div className="text-black fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center bg-black/50" onClick={onClose}>
			<div className="m-2 max-h-[85vh] w-full overflow-y-auto rounded-lg bg-white lg:w-2/5" onClick={(event) => event.stopPropagation()}>
				<div className="m-4 flex items-center justify-between">
					<p className="text-2xl font-semibold">Select aerial image</p>
					<button type="button" className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-red-500 hover:text-white" aria-label="Close" onClick={onClose}>
						Close
					</button>
				</div>
				<div className="m-4 flex flex-col gap-3">
					{pickerItems.length === 0 ? (
						<p className="text-sm text-gray-500">No aerial images are available.</p>
					) : (
						pickerItems.map((image) => (
							<AerialImageOption
								key={image.pending ? `request-${image.id}` : image.dropboxId || image.path || image.name}
								image={image}
								isSelected={!image.pending && image.dropboxId === selectedDropboxId}
								onSelect={onSelectImage}
							/>
						))
					)}
				</div>
			</div>
		</div>
	);
};

export default AerialImagePickerModal;
